import React, { useState, useEffect, useCallback } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, Wind, Droplets, Thermometer, MapPin, CloudFog, CloudLightning, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Weather() {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationName, setLocationName] = useState('Nashville');
  const [usingUserLocation, setUsingUserLocation] = useState(false);

  const fetchWeather = useCallback(async (lat, lon) => {
    try {
      const response = await fetch(`${API_URL}/api/weather?lat=${lat}&lon=${lon}`);
      if (!response.ok) throw new Error('Weather fetch failed');
      
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      
      setWeather(data);
      setLoading(false);
    } catch (err) {
      console.log('Weather error:', err);
      setLoading(false);
    }
  }, []);

  const getLocationName = useCallback(async (lat, lon) => {
    try {
      // Use reverse geocoding to get location name
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
      );
      if (response.ok) {
        const data = await response.json();
        const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county;
        if (city) {
          setLocationName(city);
        }
      }
    } catch (err) {
      // Keep default name on error
    }
  }, []);

  useEffect(() => {
    // Try to get user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUsingUserLocation(true);
          fetchWeather(latitude, longitude);
          getLocationName(latitude, longitude);
        },
        () => {
          // User denied or error - use Nashville default
          fetchWeather(36.1627, -86.7816);
        },
        { timeout: 5000 }
      );
    } else {
      // Geolocation not supported - use Nashville default
      fetchWeather(36.1627, -86.7816);
    }

    // Refresh every 30 minutes
    const interval = setInterval(() => {
      if (weather?.location) {
        fetchWeather(weather.location.lat, weather.location.lon);
      }
    }, 30 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchWeather, getLocationName, weather?.location]);

  const getWeatherIcon = (iconName) => {
    const iconMap = {
      'sun': <Sun className="w-8 h-8" style={{ color: '#FFD700' }} />,
      'cloud': <Cloud className="w-8 h-8" style={{ color: '#A0AEC0' }} />,
      'cloud-sun': <Sun className="w-8 h-8" style={{ color: '#FFD700' }} />,
      'cloud-rain': <CloudRain className="w-8 h-8" style={{ color: '#4299E1' }} />,
      'cloud-drizzle': <CloudRain className="w-8 h-8" style={{ color: '#63B3ED' }} />,
      'cloud-snow': <CloudSnow className="w-8 h-8" style={{ color: '#E2E8F0' }} />,
      'cloud-fog': <CloudFog className="w-8 h-8" style={{ color: '#A0AEC0' }} />,
      'cloud-lightning': <CloudLightning className="w-8 h-8" style={{ color: '#F6E05E' }} />,
    };
    return iconMap[iconName] || <Sun className="w-8 h-8" style={{ color: '#FFD700' }} />;
  };

  if (loading) {
    return (
      <div className="glass rounded-2xl p-4" data-testid="weather-panel">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary)' }} />
        </div>
      </div>
    );
  }

  if (!weather) {
    return null;
  }

  return (
    <div 
      className="glass rounded-2xl p-4"
      data-testid="weather-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <Thermometer className="w-5 h-5" style={{ color: 'var(--primary)' }} />
        <h3 
          className="text-lg font-bold"
          style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
        >
          {locationName} Weather
        </h3>
        {usingUserLocation && (
          <MapPin className="w-3 h-3" style={{ color: 'var(--secondary)' }} title="Using your location" />
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {getWeatherIcon(weather.icon)}
          <div>
            <p 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Teko, sans-serif', color: 'var(--text-main)' }}
            >
              {weather.temperature}°F
            </p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {weather.description}
            </p>
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            <Droplets className="w-3 h-3" />
            <span>{weather.humidity}%</span>
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            <Wind className="w-3 h-3" />
            <span>{weather.wind_speed} mph</span>
          </div>
        </div>
      </div>
    </div>
  );
}
